// src/feature-module/subscription/subscriptionRenewal.jsx
import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Card,
  Row,
  Col,
  Tag,
  Space,
  Button,
  Switch,
  Alert,
  Typography,
  Skeleton,
  Modal,
  message,
  Result,
  Divider,
  Empty,
  Segmented,
  Input,
  Form,
  InputNumber,
  Collapse,
  List,
} from "antd";
import {
  ThunderboltOutlined,
  ExclamationCircleOutlined,
  DollarCircleOutlined,
  SyncOutlined,
  StopOutlined,
  CrownOutlined,
  ArrowRightOutlined,
  SafetyCertificateOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  TeamOutlined,
  ShopOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ChevronUp, RotateCcw } from "feather-icons-react/build/IconComponents";

import {
  getLatestFeatureSubscription,
  previewNextFeatureRenewal,
  setFeatureAutoRenew,
  cancelFeatureSubscription,
  startFeatureSubscriptionWithProvider,
  listPosInventoryPackages,
} from "../../api/subscriptionApi";
import {
  getCurrentUserPerms,
  hasWrite,
  isCurrentUserAdmin,
} from "../../utils/perm";
import { setToogleHeader } from "../../core/redux/action";
import { useDispatch } from "react-redux";

const { Title, Text } = Typography;

const t = (s) =>
  typeof window !== "undefined" && typeof window.translate === "function"
    ? window.translate(s)
    : s;

const getPermissionDeniedText = () => {
  const lang = (localStorage.getItem("lang") || "en").toLowerCase();
  return lang.startsWith("ar")
    ? "ليس لديك صلاحية للوصول إلى اشتراك نقاط البيع. يرجى التواصل مع المدير."
    : "You don't have permission to access POS Subscription. Please contact your admin.";
};

const isPermissionDeniedError = (e) => {
  const status = e?.response?.status;
  if (!(status === 401 || status === 403)) return false;

  const raw = e?.response?.data || {};
  const msg = String(raw?.message || raw?.error || "").toLowerCase();

  return (
    status === 403 ||
    /permission|forbidden|access\s*denied|not\s*allowed|insufficient|unauthorized/.test(
      msg,
    ) ||
    /صلاحية|غير\s*مسموح/.test(msg)
  );
};

const showPermissionDeniedToast = (e) => {
  if (e?.__permissionToastShown) return;
  message.error({
    content: getPermissionDeniedText(),
    key: "pos-subscription-permission-denied",
    duration: 4,
  });
};

const showWriteDeniedToast = () => {
  message.error(t("You don't have permission to modify subscription"));
};

function money(v, ccy = "KWD") {
  if (v == null) return "—";
  const n = Number(v);
  return `${ccy} ${n.toLocaleString()}`;
}

function parsePairId(id = "") {
  const [providerPackageId, providerPackageOptionId] = String(id).split(":");
  return { providerPackageId, providerPackageOptionId };
}

function getRowPrice(row, billing) {
  if (!row) return 0;
  if (row.price && typeof row.price === "object") {
    const v = row.price[billing];
    return Number(v || 0);
  }
  return Number(row.discounted || 0);
}

function normalizeName(s = "") {
  return String(s).trim().toLowerCase();
}

function isFreeTrialOptionLike(opt = {}) {
  const haystack = normalizeName(
    [opt?.name_en, opt?.name_ar, opt?.value_en, opt?.value_ar]
      .filter(Boolean)
      .join(" "),
  );
  return (
    haystack.includes("free trial") ||
    haystack.includes("free trail") ||
    haystack.includes("تجريب")
  );
}

function getPlanPrice(row, billing) {
  return isFreeTrialOptionLike(row?.opt) ? 0 : getRowPrice(row, billing);
}

function limitItemsFromSubscription(sub) {
  const limits = sub?.packageOption?.limits || {};
  return [
    {
      key: "products",
      label: t("Products"),
      value: limits.productLimit,
      icon: <AppstoreOutlined />,
      color: "#1677ff",
    },
    {
      key: "users",
      label: t("Users"),
      value: limits.userAccessLimit,
      icon: <TeamOutlined />,
      color: "#52c41a",
    },
    {
      key: "stores",
      label: t("Stores"),
      value: limits.storeLimit,
      icon: <ShopOutlined />,
      color: "#fa8c16",
    },
    {
      key: "warehouses",
      label: t("Warehouses"),
      value: limits.warehouseLimit,
      icon: <DatabaseOutlined />,
      color: "#722ed1",
    },
  ].filter((item) => item.value != null);
}

function StatMiniCard({ icon, label, value, color }) {
  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 14,
        height: "100%",
        boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
      }}
      bodyStyle={{ padding: 16 }}
    >
      <Space align="start" size={12}>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `${color}15`,
            color,
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {label}
          </Text>
          <div>
            <Text strong style={{ fontSize: 20 }}>
              {value}
            </Text>
          </div>
        </div>
      </Space>
    </Card>
  );
}

StatMiniCard.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
};

function Hero({ sub, preview, active, daysLeft, onOpenPlans, canWrite, subscriptionState }) {
  const isTrial = Boolean(sub?.trail_bases);
  const nextLabel = isTrial && active ? t("Trial ends") : t("Next billing");
  const amountLabel = isTrial && active ? t("Due after trial") : t("Billed amount");
  const renewLabel = isTrial && active ? t("Due on") : t("Renews on");
  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 16,
        overflow: "hidden",
        background:
          "linear-gradient(135deg, #0f172a 0%, #1677ff 50%, #69b1ff 100%)",
        boxShadow: "0 10px 28px rgba(22,119,255,0.18)",
      }}
      bodyStyle={{ padding: 20 }}
    >
      <Row gutter={[20, 20]} align="middle">
        <Col xs={24} lg={15}>
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Space wrap size={[8, 8]}>
              <Tag
                color={active ? "success" : "error"}
                style={{ borderRadius: 999, padding: "4px 10px" }}
              >
                {isTrial && active
                  ? t("Free Trial Active")
                  : active
                    ? t("Active Subscription")
                    : isTrial || subscriptionState === "trial_expired"
                      ? t("Trial Ended")
                      : t("Inactive Subscription")}
              </Tag>

              {isTrial && (
                <Tag
                  color={active ? "cyan" : "default"}
                  style={{ borderRadius: 999, padding: "4px 10px" }}
                >
                  {t("14-Day Trial")}
                </Tag>
              )}

              {Number.isFinite(daysLeft) && (
                <Tag
                  color="gold"
                  style={{ borderRadius: 999, padding: "4px 10px" }}
                >
                  {daysLeft} {t("days left")}
                </Tag>
              )}
            </Space>

            <div>
              <Text style={{ color: "rgba(255,255,255,0.78)", fontSize: 13 }}>
                {t("Current Plan")}
              </Text>
              <Title level={3} style={{ margin: "6px 0", color: "#fff" }}>
                <Space size={8}>
                  <CrownOutlined />
                  {sub?.packageName || "—"}
                </Space>
              </Title>
              <Text style={{ color: "rgba(255,255,255,0.88)", fontSize: 14 }}>
                {(sub?.interval || "yearly").toUpperCase()} ·{" "}
                {nextLabel}{" "}
                {preview?.nextBillingAt
                  ? dayjs(preview.nextBillingAt).format("DD MMM YYYY")
                  : "—"}
              </Text>
            </div>

            <Space wrap size={[8, 8]}>
              <Tag
                icon={<CreditCardOutlined />}
                style={{
                  borderRadius: 999,
                  padding: "6px 12px",
                  background: "rgba(255,255,255,0.14)",
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.2)",
                }}
              >
                {t("Auto Renew")}:{" "}
                {sub?.autoRenew ? t("Enabled") : t("Disabled")}
              </Tag>

              <Tag
                icon={<CalendarOutlined />}
                style={{
                  borderRadius: 999,
                  padding: "6px 12px",
                  background: "rgba(255,255,255,0.14)",
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.2)",
                }}
              >
                {t("Billing Cycle")}:{" "}
                {(sub?.interval || "yearly").toUpperCase()}
              </Tag>
            </Space>
          </Space>
        </Col>

        <Col xs={24} lg={9}>
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              background: "rgba(255,255,255,0.14)",
              backdropFilter: "blur(10px)",
            }}
            bodyStyle={{ padding: 18 }}
          >
            <Space
              direction="vertical"
              size={10}
              style={{ width: "100%", textAlign: "center" }}
            >
              <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                {amountLabel}
              </Text>
              <Title level={2} style={{ margin: 0, color: "#fff" }}>
                {money(preview?.amount, preview?.currency || sub?.currency)}
              </Title>
              <Text style={{ color: "rgba(255,255,255,0.75)" }}>
                {preview?.nextBillingAt
                  ? `${renewLabel} ${dayjs(preview.nextBillingAt).format(
                      "DD MMM YYYY",
                    )}`
                  : t("Billing date unavailable")}
              </Text>
              <Button
                type="primary"
                size="large"
                block
                onClick={onOpenPlans}
                disabled={!canWrite}
                style={{
                  borderRadius: 10,
                  height: 42,
                  fontWeight: 600,
                  background: "#fff",
                  color: "#1677ff",
                  borderColor: "#fff",
                }}
              >
                {t("Upgrade / Change Plan")}
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </Card>
  );
}

Hero.propTypes = {
  sub: PropTypes.object,
  preview: PropTypes.object,
  active: PropTypes.bool,
  daysLeft: PropTypes.number,
  onOpenPlans: PropTypes.func.isRequired,
  canWrite: PropTypes.bool,
  subscriptionState: PropTypes.string,
};

function PlanCard({ row, billing, selecting, onChoose, currency, canWrite }) {
  const pkg = row.pkg;
  const opt = row.opt;
  const isFreeTrial = isFreeTrialOptionLike(opt);
  const price = getPlanPrice(row, billing);

  const limits = [
    { label: t("Products"), value: opt?.limits?.productLimit },
    { label: t("Users"), value: opt?.limits?.userAccessLimit },
    { label: t("Stores"), value: opt?.limits?.storeLimit },
    { label: t("Warehouses"), value: opt?.limits?.warehouseLimit },
  ].filter((item) => item.value != null);

  return (
    <Card
      hoverable
      style={{
        borderRadius: 14,
        height: "100%",
        border: "1px solid #f0f0f0",
      }}
      bodyStyle={{ padding: 16 }}
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {pkg?.name_en || "Plan"}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {opt?.name_en || "Base"}
            </Text>
          </div>

          <Tag
            color={isFreeTrial ? "green" : "blue"}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              fontWeight: 700,
              marginInlineEnd: 0,
            }}
          >
            {isFreeTrial ? t("14 Days Free") : money(price, currency)}
          </Tag>
        </div>

        <Text type="secondary" style={{ minHeight: 36, display: "block" }}>
          {opt?.value_en || t("Professional subscription package")}
        </Text>

        <div
          style={{
            padding: 12,
            borderRadius: 12,
            background: "#fafafa",
            border: "1px solid #f0f0f0",
          }}
        >
          <List
            size="small"
            dataSource={limits}
            locale={{ emptyText: t("No limits defined") }}
            renderItem={(item) => (
              <List.Item style={{ paddingInline: 0 }}>
                <Space
                  style={{
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Text type="secondary">{item.label}</Text>
                  <Text strong>{item.value}</Text>
                </Space>
              </List.Item>
            )}
          />
        </div>

        <Button
          type="primary"
          block
          size="large"
          loading={selecting}
          disabled={!canWrite}
          style={{
            borderRadius: 10,
            height: 42,
            fontWeight: 600,
          }}
          onClick={() =>
            onChoose({
              providerPackageId: pkg.id,
              providerPackageOptionId: opt?.id,
            })
          }
        >
          {isFreeTrial ? t("Start 14-Day Free Trial") : t("Choose Plan")}
        </Button>

        <Text
          type="secondary"
          style={{ fontSize: 12, textAlign: "center", display: "block" }}
        >
          {isFreeTrial
            ? t("No payment now. Paid renewal starts after the trial.")
            : billing === "monthly"
              ? t("Billed monthly")
              : t("Billed yearly")}
        </Text>
      </Space>
    </Card>
  );
}

PlanCard.propTypes = {
  row: PropTypes.object.isRequired,
  billing: PropTypes.oneOf(["monthly", "yearly"]).isRequired,
  selecting: PropTypes.bool,
  onChoose: PropTypes.func.isRequired,
  currency: PropTypes.string,
  canWrite: PropTypes.bool,
};

const SubscriptionRenewal = () => {
  const dispatch = useDispatch();
  const headerCollapsed = useSelector(
    (state) => state.rootReducer.toggle_header,
  );
  const user = useSelector((s) => s?.auth?.user);

  const userPerms = getCurrentUserPerms(user?.permissions || []);
  const canWriteSaas =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasWrite(userPerms, "saas");

  const [creds, setCreds] = React.useState({
    storeId: null,
    email: "",
    password: "",
  });

  const [loading, setLoading] = React.useState(false);
  const [sub, setSub] = React.useState(null);
  const [active, setActive] = React.useState(false);
  const [daysLeft, setDaysLeft] = React.useState(null);
  const [subscriptionState, setSubscriptionState] = React.useState("inactive");
  const [preview, setPreview] = React.useState(null);

  const [autoRenew, setAutoRenew] = React.useState(false);
  const [autoRenewSaving, setAutoRenewSaving] = React.useState(false);

  const [suggestions, setSuggestions] = React.useState([]);

  const [packagesLoading, setPackagesLoading] = React.useState(false);
  const [pkgRows, setPkgRows] = React.useState([]);
  const [showPlans, setShowPlans] = React.useState(false);
  const [selecting, setSelecting] = React.useState(false);

  const [billingView, setBillingView] = React.useState("yearly");
  const [planFilter, setPlanFilter] = React.useState("All");
  const [search, setSearch] = React.useState("");

  const [form] = Form.useForm();

  const features = React.useMemo(() => ["website", "pos", "inventory"], []);
  const canRun = React.useMemo(
    () => Boolean(creds.storeId) && Boolean(creds.email),
    [creds.storeId, creds.email],
  );

  const load = React.useCallback(
    async (params) => {
      const storeId = params?.storeId ?? creds.storeId;
      const email = params?.email ?? creds.email;
      const localCanRun = Boolean(storeId) && Boolean(email);

      if (!localCanRun) return;

      setLoading(true);

      try {
        const latest = await getLatestFeatureSubscription({
          storeId,
          email,
          features,
        });

        setSub(latest.subscription);
        setActive(Boolean(latest.isActive));
        setDaysLeft(latest.daysLeft);
        setSubscriptionState(latest.subscriptionState || "inactive");
        setSuggestions(latest.suggestions || []);
        setAutoRenew(Boolean(latest.subscription?.autoRenew));
      } catch (e) {
        if (isPermissionDeniedError(e)) {
          showPermissionDeniedToast(e);
          setSub(null);
          setSubscriptionState("inactive");
          setLoading(false);
          return;
        }

        const status = e?.response?.status;
        message.warning(
          status === 404
            ? t("No active subscription.")
            : e?.response?.data?.error ||
                e.message ||
                t("Failed to load subscription"),
        );

        setSub(null);
        setSubscriptionState("inactive");
        setLoading(false);
        return;
      }

      try {
        const prev = await previewNextFeatureRenewal({
          storeId,
          email,
          features,
        });
        setPreview(prev);
      } catch (e) {
        setPreview(null);
      } finally {
        setLoading(false);
      }
    },
    [creds.storeId, creds.email, features],
  );

  const onSubmitCreds = (vals) => {
    const next = {
      storeId: vals.storeId,
      email: vals.email?.trim(),
      password: vals.password || "",
    };
    setCreds(next);
    load(next);
  };

  async function onToggleAutoRenew(checked) {
    if (!canWriteSaas) {
      showWriteDeniedToast();
      return;
    }

    if (!sub) return;

    try {
      setAutoRenewSaving(true);
      setAutoRenew(checked);

      await setFeatureAutoRenew({
        storeId: creds.storeId,
        email: creds.email,
        features,
        enable: checked,
        password: creds.password || "",
      });

      setSub((prev) => (prev ? { ...prev, autoRenew: checked } : prev));
      message.success(
        checked ? t("Auto-renew enabled") : t("Auto-renew disabled"),
      );
    } catch (e) {
      if (isPermissionDeniedError(e)) {
        showPermissionDeniedToast(e);
        return;
      }
      setAutoRenew((prev) => !prev);
      message.error(
        e?.response?.data?.error || t("Failed to update auto-renew"),
      );
    } finally {
      setAutoRenewSaving(false);
    }
  }

  async function onCancel() {
    if (!canWriteSaas) {
      showWriteDeniedToast();
      return;
    }

    Modal.confirm({
      title: t("Cancel subscription?"),
      icon: <ExclamationCircleOutlined />,
      content: t("Access stays active until the current period ends."),
      okText: t("Cancel Subscription"),
      okType: "danger",
      onOk: async () => {
        try {
          await cancelFeatureSubscription({
            storeId: creds.storeId,
            email: creds.email,
            features,
          });
          message.success(t("Subscription will not renew."));
          await load();
        } catch (e) {
          if (isPermissionDeniedError(e)) {
            showPermissionDeniedToast(e);
            return;
          }
          message.error(e?.response?.data?.error || t("Cancel failed"));
        }
      },
    });
  }

  async function beginCheckout({ providerPackageId, providerPackageOptionId }) {
    if (!canWriteSaas) {
      showWriteDeniedToast();
      return;
    }

    try {
      setSelecting(true);
      const selectedRow = pkgRows.find(
        (row) =>
          String(row?.pkg?.id) === String(providerPackageId) &&
          String(row?.opt?.id ?? "") === String(providerPackageOptionId ?? ""),
      );
      const isFreeTrial = isFreeTrialOptionLike(selectedRow?.opt);
      const r = await startFeatureSubscriptionWithProvider({
        storeId: creds.storeId,
        email: creds.email,
        providerPackageId,
        providerPackageOptionId,
        features,
        autoRenew: !isFreeTrial,
      });

      if (r?.url) window.location.href = r.url;
      else message.error(t("Failed to start checkout"));
    } catch (e) {
      if (isPermissionDeniedError(e)) {
        showPermissionDeniedToast(e);
        return;
      }
      message.error(
        e?.response?.data?.error || e.message || t("Checkout failed"),
      );
    } finally {
      setSelecting(false);
    }
  }

  async function openPlans() {
    if (!canWriteSaas) {
      showWriteDeniedToast();
      return;
    }

    setShowPlans(true);
    if (pkgRows.length) return;

    try {
      setPackagesLoading(true);
      const rows = await listPosInventoryPackages();

      const currentName = normalizeName(sub?.packageName || "");
      const filtered = Array.isArray(rows)
        ? currentName
          ? rows.filter(
              (r) => normalizeName(r?.pkg?.name_en || "") === currentName,
            )
          : rows
        : [];

      setPkgRows(
        filtered.map((row, index) => ({
          ...row,
          id: row.id || `${row?.pkg?.id || "pkg"}-${row?.opt?.id || index}`,
          searchText: `${row?.pkg?.name_en || ""} ${row?.opt?.name_en || ""} ${
            row?.opt?.value_en || ""
          }`.toLowerCase(),
        })),
      );
    } catch (e) {
      if (isPermissionDeniedError(e)) {
        showPermissionDeniedToast(e);
        return;
      }
      message.error(t("Failed to load packages"));
    } finally {
      setPackagesLoading(false);
    }
  }

  async function reloadPlans() {
    setPkgRows([]);
    setShowPlans(true);

    try {
      setPackagesLoading(true);
      const rows = await listPosInventoryPackages();

      const currentName = normalizeName(sub?.packageName || "");
      const filtered = Array.isArray(rows)
        ? currentName
          ? rows.filter(
              (r) => normalizeName(r?.pkg?.name_en || "") === currentName,
            )
          : rows
        : [];

      setPkgRows(
        filtered.map((row, index) => ({
          ...row,
          id: row.id || `${row?.pkg?.id || "pkg"}-${row?.opt?.id || index}`,
          searchText: `${row?.pkg?.name_en || ""} ${row?.opt?.name_en || ""} ${
            row?.opt?.value_en || ""
          }`.toLowerCase(),
        })),
      );
    } catch (e) {
      if (isPermissionDeniedError(e)) {
        showPermissionDeniedToast(e);
        return;
      }
      message.error(t("Failed to load packages"));
    } finally {
      setPackagesLoading(false);
    }
  }

  const filteredRows = React.useMemo(() => {
    let rows = pkgRows.slice();

    if (planFilter !== "All") {
      rows = rows.filter((r) =>
        (r.opt?.name_en || "Base")
          .toLowerCase()
          .includes(planFilter.toLowerCase()),
      );
    }

    if (search.trim()) {
      rows = rows.filter((r) =>
        (r.searchText || "").includes(search.trim().toLowerCase()),
      );
    }

    rows.sort(
      (a, b) => getPlanPrice(a, billingView) - getPlanPrice(b, billingView),
    );
    return rows;
  }, [pkgRows, planFilter, search, billingView]);

  const currency = preview?.currency || sub?.currency || "KWD";
  const limitCards = limitItemsFromSubscription(sub);

  const renderRefreshTooltip = (props) => (
    <Tooltip id="subscription-refresh-tooltip" {...props}>
      {t("Refresh")}
    </Tooltip>
  );

  const renderCollapseTooltip = (props) => (
    <Tooltip id="subscription-collapse-tooltip" {...props}>
      {t("Collapse")}
    </Tooltip>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <style>{`
          .subscription-page .ant-card,
          .subscription-page .ant-collapse,
          .subscription-page .ant-alert,
          .subscription-page .ant-result {
            border-radius: 14px;
          }

          .subscription-page .ant-card {
            width: 100%;
          }

          .subscription-page .subscription-form-card .ant-card-head {
            min-height: 58px;
          }

          .subscription-page .subscription-form-card .ant-card-head-title,
          .subscription-page .subscription-section-card .ant-card-head-title {
            font-weight: 600;
          }

          .subscription-page .subscription-actions .ant-btn {
            border-radius: 10px;
            height: 40px;
          }

          .subscription-page .subscription-top-tools {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .subscription-page .subscription-result-card .ant-result {
            padding: 44px 24px;
          }

          .subscription-page .subscription-collapse .ant-collapse-header {
            font-weight: 600;
          }

          @media (max-width: 991.98px) {
            .subscription-page .subscription-top-tools {
              justify-content: flex-start;
            }
          }
        `}</style>

        <div className="subscription-page">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>{t("POS Subscription")}</h4>
                <h6>
                  {t("Manage your plan, billing cycle, and renewal settings")}
                </h6>
              </div>
            </div>

            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (canRun) load();
                    }}
                  >
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                  <Link
                    to="#"
                    id="collapse-header"
                    className={headerCollapsed ? "active" : ""}
                    onClick={(e) => {
                      e.preventDefault();
                      dispatch(setToogleHeader(!headerCollapsed));
                    }}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>

          <div className="card table-list-card subscription-section-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div>
                <Text strong>{t("Subscription Controls")}</Text>
              </div>

              <div className="subscription-top-tools subscription-actions">
                <Space wrap>
                  <Card
                    size="small"
                    style={{
                      borderRadius: 12,
                      background: "#fafafa",
                      border: "1px solid #f0f0f0",
                      marginBottom: 0,
                    }}
                    bodyStyle={{ padding: "8px 12px" }}
                  >
                    <Space>
                      <Text type="secondary">{t("Auto-renew")}</Text>
                      <Switch
                        checked={autoRenew}
                        loading={autoRenewSaving}
                        disabled={!sub || !canWriteSaas || !!sub?.trail_bases}
                        onChange={onToggleAutoRenew}
                      />
                    </Space>
                  </Card>

                  <Button
                    icon={<SyncOutlined />}
                    onClick={() => load()}
                    disabled={!canRun}
                  >
                    {t("Refresh")}
                  </Button>

                  <Button
                    danger
                    icon={<StopOutlined />}
                    onClick={onCancel}
                    disabled={!sub || autoRenewSaving || !canWriteSaas}
                  >
                    {t("Cancel")}
                  </Button>

                  <Button
                    type="primary"
                    icon={<CrownOutlined />}
                    onClick={openPlans}
                    disabled={!canRun || !canWriteSaas}
                  >
                    {t("Change Plan")}
                  </Button>
                </Space>
              </div>
            </div>

            <div className="card-body">
              <Card
                className="subscription-form-card"
                bordered={false}
                title={
                  <Space>
                    <SafetyCertificateOutlined />
                    <Text strong>{t("Secure Subscription Access")}</Text>
                  </Space>
                }
                extra={
                  <Space.Compact>
                    <Button
                      onClick={() => {
                        form.resetFields();
                        setCreds({ storeId: null, email: "", password: "" });
                      }}
                    >
                      {t("Clear")}
                    </Button>
                    <Button type="primary" onClick={() => form.submit()}>
                      {t("Load")}
                    </Button>
                  </Space.Compact>
                }
                bodyStyle={{ padding: 16 }}
                style={{
                  background: "#fff",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                  marginBottom: 16,
                }}
              >
                <Form
                  form={form}
                  layout="vertical"
                  autoComplete="off"
                  initialValues={{
                    storeId: creds.storeId,
                    email: creds.email,
                    password: "",
                  }}
                  onFinish={onSubmitCreds}
                >
                  <Row gutter={[16, 8]}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label={t("Store ID")}
                        name="storeId"
                        rules={[
                          {
                            required: true,
                            message: t("Store ID is required"),
                          },
                        ]}
                      >
                        <InputNumber
                          size="large"
                          style={{ width: "100%" }}
                          placeholder="e.g. 455564"
                          controls={false}
                          min={1}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={10}>
                      <Form.Item
                        label={t("Email")}
                        name="email"
                        rules={[
                          {
                            required: true,
                            type: "email",
                            message: t("Valid email required"),
                          },
                        ]}
                      >
                        <Input size="large" placeholder="you@domain.com" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={6}>
                      <Form.Item label={t("Password")} name="password">
                        <Input.Password
                          size="large"
                          placeholder={t("Optional")}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Alert
                    type="info"
                    showIcon
                    message={t(
                      "Your credentials are used only for this request and are not stored in the browser.",
                    )}
                  />
                </Form>
              </Card>

              {!canRun ? (
                <Card
                  className="subscription-result-card"
                  bordered={false}
                  style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}
                >
                  <Result
                    icon={
                      <ExclamationCircleOutlined style={{ color: "#1677ff" }} />
                    }
                    title={t(
                      "Enter Store ID and Email to view your subscription",
                    )}
                    subTitle={t(
                      "Load your current package details, renewal preview, and available upgrade plans.",
                    )}
                    extra={
                      <Button type="primary" onClick={() => form.submit()}>
                        {t("Load Subscription")}
                      </Button>
                    }
                  />
                </Card>
              ) : loading ? (
                <Card
                  bordered={false}
                  style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}
                >
                  <Skeleton active paragraph={{ rows: 8 }} />
                </Card>
              ) : !sub ? (
                <Card
                  bordered={false}
                  style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}
                >
                  <Result
                    status="404"
                    title={t("No subscription found")}
                    subTitle={t(
                      "Start a subscription to unlock POS, website, and inventory features.",
                    )}
                    extra={
                      <Button
                        type="primary"
                        onClick={openPlans}
                        disabled={!canWriteSaas}
                      >
                        {t("View Plans")}
                      </Button>
                    }
                  />
                </Card>
              ) : (
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                  <Hero
                    sub={sub}
                    preview={preview}
                    active={active}
                    daysLeft={daysLeft}
                    onOpenPlans={openPlans}
                    canWrite={canWriteSaas}
                    subscriptionState={subscriptionState}
                  />

                  {!!limitCards.length && (
                    <Row gutter={[16, 16]}>
                      {limitCards.map((item) => (
                        <Col key={item.key} xs={24} sm={12} lg={6}>
                          <StatMiniCard
                            icon={item.icon}
                            label={item.label}
                            value={item.value}
                            color={item.color}
                          />
                        </Col>
                      ))}
                    </Row>
                  )}

                  <Card
                    bordered={false}
                    title={
                      <Space>
                        <ThunderboltOutlined />
                        <Text strong>{t("Recommended Upgrades")}</Text>
                      </Space>
                    }
                    style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}
                  >
                    {!suggestions?.length ? (
                      <Empty description={t("No suggestions right now")} />
                    ) : (
                      <Row gutter={[16, 16]}>
                        {suggestions.slice(0, 6).map((sugg) => (
                          <Col key={sugg.id} xs={24} sm={12} lg={8}>
                            <Card
                              hoverable
                              style={{
                                borderRadius: 14,
                                height: "100%",
                                border: "1px solid #f0f0f0",
                              }}
                              bodyStyle={{ padding: 16 }}
                            >
                              <Space
                                direction="vertical"
                                size={12}
                                style={{ width: "100%" }}
                              >
                                <Space
                                  align="start"
                                  style={{
                                    width: "100%",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <div>
                                    <Text strong style={{ fontSize: 16 }}>
                                      {sugg.name}
                                    </Text>
                                    <div>
                                      <Text
                                        type="secondary"
                                        style={{ fontSize: 12 }}
                                      >
                                        {t("Suggested for your usage")}
                                      </Text>
                                    </div>
                                  </div>

                                  <Tag
                                    color="blue"
                                    style={{
                                      padding: "6px 10px",
                                      borderRadius: 999,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {money(sugg?.price?.discounted, currency)}
                                  </Tag>
                                </Space>

                                <div
                                  style={{
                                    background: "#fafafa",
                                    border: "1px solid #f0f0f0",
                                    borderRadius: 12,
                                    padding: 12,
                                  }}
                                >
                                  <Row gutter={[10, 10]}>
                                    <Col span={12}>
                                      <Text type="secondary">
                                        {t("Products")}
                                      </Text>
                                      <div>
                                        <Text strong>
                                          {sugg?.limits?.productLimit ?? "—"}
                                        </Text>
                                      </div>
                                    </Col>
                                    <Col span={12}>
                                      <Text type="secondary">{t("Users")}</Text>
                                      <div>
                                        <Text strong>
                                          {sugg?.limits?.userAccessLimit ?? "—"}
                                        </Text>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>

                                <Text type="secondary" style={{ fontSize: 13 }}>
                                  {sugg.reason}
                                </Text>

                                <Button
                                  type="primary"
                                  icon={<ArrowRightOutlined />}
                                  disabled={!canWriteSaas}
                                  onClick={() => {
                                    const {
                                      providerPackageId,
                                      providerPackageOptionId,
                                    } = parsePairId(sugg.id);
                                    beginCheckout({
                                      providerPackageId,
                                      providerPackageOptionId,
                                    });
                                  }}
                                >
                                  {t("Upgrade Now")}
                                </Button>
                              </Space>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </Card>
                </Space>
              )}

              <div style={{ marginTop: 16 }}>
                <Collapse
                  className="subscription-collapse"
                  activeKey={showPlans ? ["plans"] : []}
                  onChange={(keys) => setShowPlans(keys.includes("plans"))}
                  style={{
                    borderRadius: 14,
                    overflow: "hidden",
                    background: "#fff",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                  }}
                  items={[
                    {
                      key: "plans",
                      label: (
                        <Space>
                          <DollarCircleOutlined />
                          <Text strong>{t("Available Plans")}</Text>
                        </Space>
                      ),
                      children: (
                        <div>
                          <Row
                            gutter={[12, 12]}
                            align="middle"
                            justify="space-between"
                          >
                            <Col xs={24} xl={18}>
                              <Space wrap size={[10, 10]}>
                                <Segmented
                                  value={billingView}
                                  onChange={setBillingView}
                                  options={[
                                    { label: t("Monthly"), value: "monthly" },
                                    { label: t("Yearly"), value: "yearly" },
                                  ]}
                                />

                                <Segmented
                                  value={planFilter}
                                  onChange={setPlanFilter}
                                  options={[
                                    { label: t("All"), value: "All" },
                                    { label: t("Basic"), value: "Basic" },
                                    { label: t("Starter"), value: "Starter" },
                                    {
                                      label: t("Professional"),
                                      value: "Professional",
                                    },
                                    { label: t("Business"), value: "Business" },
                                  ]}
                                />

                                <Input.Search
                                  allowClear
                                  placeholder={t("Search plan")}
                                  value={search}
                                  onChange={(e) => setSearch(e.target.value)}
                                  style={{ width: 260, maxWidth: "100%" }}
                                />
                              </Space>
                            </Col>

                            <Col xs={24} xl="auto">
                              <Button
                                icon={<SyncOutlined />}
                                loading={packagesLoading}
                                onClick={reloadPlans}
                              >
                                {t("Reload")}
                              </Button>
                            </Col>
                          </Row>

                          <Divider />

                          {packagesLoading ? (
                            <Skeleton active paragraph={{ rows: 8 }} />
                          ) : !filteredRows.length ? (
                            <Empty description={t("No plans available")} />
                          ) : (
                            <Row gutter={[16, 16]}>
                              {filteredRows.map((row) => (
                                <Col key={row.id} xs={24} sm={12} lg={8} xl={6}>
                                  <PlanCard
                                    row={row}
                                    billing={billingView}
                                    selecting={selecting}
                                    onChoose={beginCheckout}
                                    currency={currency}
                                    canWrite={canWriteSaas}
                                  />
                                </Col>
                              ))}
                            </Row>
                          )}

                          {billingView === "monthly" && (
                            <Alert
                              style={{ marginTop: 16 }}
                              type="info"
                              showIcon
                              message={t(
                                "Monthly pricing will show when provided by the backend.",
                              )}
                            />
                          )}
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRenewal;
